import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

export default function Checkout() {
  const { cart, cartTotal, clearCart } = useStore();
  const [form, setForm] = useState({ full_name: "", phone: "", address: "", city: "", notes: "" });
  const [placed, setPlaced] = useState(false);
  const [busy, setBusy] = useState(false);
  const [promo, setPromo] = useState("");
  const [discount, setDiscount] = useState(null);
  const [promoErr, setPromoErr] = useState("");

  const applyPromo = async () => {
    setPromoErr("");
    if (!promo.trim()) return;
    try {
      const { data } = await api.post("/discounts/validate", { code: promo, subtotal: cartTotal });
      setDiscount(data);
      toast.success("Promo code applied");
    } catch (err) { setDiscount(null); setPromoErr(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) return;
    setBusy(true);
    try {
      await api.post("/orders", { ...form, items: cart, total: cartTotal, discount_code: discount?.code || null });
      clearCart();
      setPlaced(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (placed) {
    return (
      <div className="max-w-xl mx-auto px-5 py-32 min-h-[70vh] text-center">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", duration: 0.7 }} className="w-16 h-16 border border-ink rounded-full flex items-center justify-center mx-auto mb-8">
          <Check strokeWidth={1.5} className="w-7 h-7" />
        </motion.div>
        <h1 className="font-serif-display text-4xl font-light mb-4" data-testid="order-success">Order request received</h1>
        <p className="text-muted-foreground mb-8 leading-relaxed">Thank you. We'll reach out shortly via phone to confirm your order and arrange payment & delivery.</p>
        <Link to="/shop" className="label-caps border-b border-ink pb-1 hover:text-wine hover:border-wine transition-colors" data-testid="continue-shopping-link">Continue shopping</Link>
      </div>
    );
  }

  if (cart.length === 0) {
    return <div className="max-w-xl mx-auto px-5 py-32 min-h-[70vh] text-center"><p className="font-serif-display text-3xl">Your bag is empty.</p><Link to="/shop" className="label-caps border-b border-ink mt-4 inline-block">Shop now</Link></div>;
  }

  return (
    <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-16 min-h-[70vh]">
      <h1 className="font-serif-display text-5xl font-light tracking-tighter mb-4">Checkout</h1>
      <p className="text-muted-foreground mb-12 max-w-md text-sm">No payment is taken online. Submit your details and we'll confirm your order personally.</p>
      <div className="grid lg:grid-cols-3 gap-12">
        <form onSubmit={submit} className="lg:col-span-2 space-y-6 max-w-xl" data-testid="checkout-form">
          {[
            { k: "full_name", label: "Full name", type: "text" },
            { k: "phone", label: "Phone number", type: "tel" },
            { k: "address", label: "Delivery address", type: "text" },
            { k: "city", label: "City", type: "text" },
          ].map((f) => (
            <div key={f.k}>
              <label className="label-caps text-muted-foreground">{f.label}</label>
              <input required type={f.type} value={form[f.k]} onChange={set(f.k)} data-testid={`checkout-${f.k}`}
                className="w-full bg-transparent border-b border-line py-3 text-base focus:outline-none focus:border-ink transition-colors" />
            </div>
          ))}
          <div>
            <label className="label-caps text-muted-foreground">Notes (optional)</label>
            <textarea value={form.notes} onChange={set("notes")} rows={3} data-testid="checkout-notes"
              className="w-full bg-transparent border-b border-line py-3 text-base focus:outline-none focus:border-ink transition-colors resize-none" />
          </div>
          <button type="submit" disabled={busy} className="w-full bg-ink text-pearl py-4 label-caps hover:bg-wine transition-colors disabled:opacity-50" data-testid="place-order-btn">
            {busy ? "Placing order..." : "Place order request"}
          </button>
        </form>

        <div className="lg:col-span-1">
          <div className="bg-surface p-8 sticky top-24">
            <h2 className="font-serif-display text-2xl mb-6">Your order</h2>
            <div className="space-y-4 mb-6">
              {cart.map((it, i) => (
                <div key={i} className="flex gap-3 items-center">
                  <div className="w-12 h-16 bg-line overflow-hidden shrink-0">{it.image && <img src={fileUrl(it.image)} alt="" className="img-cover" />}</div>
                  <div className="flex-1 text-sm"><p className="font-medium leading-tight">{it.name}</p><p className="text-muted-foreground text-xs">{[it.color, it.size].filter(Boolean).join(" / ")} × {it.quantity}</p></div>
                  <p className="text-sm">${(it.price * it.quantity).toFixed(0)}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-line pt-4 mt-2">
              <label className="label-caps text-muted-foreground">Promo code</label>
              <div className="flex gap-2 mt-1">
                <input value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} data-testid="promo-input" placeholder="Enter code" className="flex-1 bg-transparent border-b border-line py-2 text-base focus:outline-none focus:border-ink transition-colors" />
                <button type="button" onClick={applyPromo} data-testid="apply-promo-btn" className="px-4 border border-ink label-caps hover:bg-ink hover:text-pearl transition-colors">Apply</button>
              </div>
              {promoErr && <p className="text-destructive text-xs mt-2" data-testid="promo-error">{promoErr}</p>}
            </div>
            <div className="flex justify-between mt-5 text-sm"><span className="text-muted-foreground">Subtotal</span><span data-testid="checkout-subtotal">${cartTotal.toFixed(0)}</span></div>
            {discount && (
              <div className="flex justify-between mt-2 text-sm text-wine" data-testid="checkout-discount"><span>Discount ({discount.code})</span><span>-${discount.discount_amount.toFixed(0)}</span></div>
            )}
            <div className="flex justify-between border-t border-line pt-4 mt-4"><span className="label-caps">Total</span><span className="font-serif-display text-2xl" data-testid="checkout-total">${(cartTotal - (discount?.discount_amount || 0)).toFixed(0)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
